<template>
  <section class="fts-settings-card" v-if="plans.length">
    <div>
      <h2>What it would cost</h2>
      <p class="fts-settings-card-sub">
        Try a plan, a billing period and a number of seats. Nothing changes until you review and
        confirm, and the exact first charge is confirmed before you pay.
      </p>
    </div>

    <div class="fts-settings-estimate">
      <div class="fts-settings-estimate-controls">
        <div class="fts-settings-field">
          <span class="fts-settings-field-label">Plan</span>
          <div class="fts-settings-seg" role="group" aria-label="Plan to estimate">
            <button
              type="button"
              v-for="plan in sellablePlans"
              :key="plan.planKey"
              :aria-pressed="String(plan.planKey === planKey)"
              :class="{ 'is-on': plan.planKey === planKey }"
              @click="planKey = plan.planKey"
            >
              {{ plan.name }}
            </button>
          </div>
        </div>

        <div class="fts-settings-field">
          <span class="fts-settings-field-label">Billing period</span>
          <div class="fts-settings-seg" role="group" aria-label="Billing period">
            <button
              type="button"
              :aria-pressed="String(interval === 'month')"
              :class="{ 'is-on': interval === 'month' }"
              @click="interval = 'month'"
            >
              Monthly
            </button>
            <button
              type="button"
              :aria-pressed="String(interval === 'year')"
              :class="{ 'is-on': interval === 'year' }"
              @click="interval = 'year'"
            >
              Yearly <small>2 months free</small>
            </button>
          </div>
        </div>

        <div class="fts-settings-field">
          <span class="fts-settings-field-label">Add-on actor seats</span>
          <div class="fts-settings-stepper-row">
            <div class="fts-settings-stepper">
              <button type="button" aria-label="One seat fewer" :disabled="extraSeats <= 0" @click="extraSeats -= 1">
                <font-awesome-icon :icon="['fas', 'minus']" />
              </button>
              <output aria-live="polite">{{ extraSeats }}</output>
              <button type="button" aria-label="One seat more" :disabled="extraSeats >= slots" @click="extraSeats += 1">
                <font-awesome-icon :icon="['fas', 'plus']" />
              </button>
            </div>
            <div class="fts-settings-stepper-meta">
              <span>{{ slotLine }}</span>
              <div class="fts-settings-meter">
                <div class="fts-settings-meter-fill" :style="{ width: slotPct }"></div>
              </div>
            </div>
          </div>
          <p class="fts-settings-note">
            An actor seat is one identity that connects from outside: a person using Claude or
            ChatGPT, an automation in n8n or Zapier, or an API script. Every plan includes one.
            Seats add no records or storage.
          </p>
        </div>
      </div>

      <div class="fts-settings-estimate-total">
        <div class="fts-settings-estimate-line">
          <span>
            <strong>{{ chosen.name }} plan</strong>
            <small>{{ interval === 'year' ? 'Billed yearly' : 'Billed monthly' }}</small>
          </span>
          <span>{{ money(planAmount) }}</span>
        </div>
        <div class="fts-settings-estimate-line">
          <span>
            <strong>Actor seats</strong>
            <small>{{ seatLine }}</small>
          </span>
          <span>{{ money(seatAmount) }}</span>
        </div>
        <div class="fts-settings-estimate-rule"></div>
        <div class="fts-settings-estimate-line is-total">
          <span>
            <strong>Total</strong>
            <small>{{ interval === 'year' ? 'a year' : 'a month' }}</small>
          </span>
          <span>{{ money(total) }}</span>
        </div>

        <p class="fts-settings-estimate-delta">{{ delta }}</p>

        <button
          type="button"
          class="fts-settings-btn is-primary"
          :disabled="!subscribe && !changed"
          @click="$emit('review', { planKey: chosen.planKey, interval, extraSeats })"
        >
          {{ buttonLabel }}
        </button>
        <span class="fts-settings-note" v-if="subscribe">
          Excludes tax. Subscribing opens Stripe Checkout, which confirms the exact amount before
          anything is charged.
        </span>
        <span class="fts-settings-note" v-else-if="summary && summary.planChange === 'in_app'">
          Excludes tax. Changing plan shows Stripe's exact amount here, and nothing is charged
          until you confirm.
        </span>
        <span class="fts-settings-note" v-else>
          Excludes tax. Changing plan opens the billing portal, which confirms the exact amount
          before anything is charged.
        </span>
      </div>
    </div>
  </section>
</template>

<script>
/**
 * Settings › Plan — "What it would cost" (W-9).
 *
 * An estimate, deliberately: seats bill as they are assigned (spec §16.3 #4, Stripe quantity =
 * seats held − included), and a plan or period change is confirmed in the Stripe portal. Nothing
 * here charges anything, and every price comes from the summary rather than from page copy.
 */
const ORDER = ["free", "essential", "team", "business"];

export default {
  name: "SettingsPlanEstimate",
  props: {
    summary: Object,
    // No subscription to change yet (SS06203): the choice is bought through Checkout, so it is
    // offered even when it matches what the workspace had before (a cancelled plan, re-bought).
    subscribe: Boolean,
  },
  data() {
    return {
      planKey: null,
      interval: "month",
      extraSeats: null,
    };
  },
  computed: {
    plans() {
      return ((this.summary && this.summary.plans) || [])
        .slice()
        .sort((a, b) => this.rank(a.planKey) - this.rank(b.planKey));
    },
    sellablePlans() {
      return this.plans.filter(p => p.sellable).map(p => ({
        planKey: p.planKey,
        name: this.planName(p.planKey),
      }));
    },
    chosen() {
      const key = this.planKey || this.defaultPick;
      const plan = this.plans.find(p => p.planKey === key) || this.plans[0] || {};
      return { planKey: plan.planKey, name: this.planName(plan.planKey), raw: plan };
    },
    /** What the workspace is actually on -- which may be a rung nobody can buy here. */
    currentPlanKey() {
      return (this.summary && this.summary.planKey) || "free";
    },
    /** What the picker opens on: the current plan when it is sellable, else the cheapest. */
    defaultPick() {
      const sellable = this.sellablePlans.map(p => p.planKey);
      return sellable.indexOf(this.currentPlanKey) >= 0 ? this.currentPlanKey : sellable[0];
    },
    currentPlanName() {
      return this.planName(this.currentPlanKey);
    },
    seats() {
      return (this.summary && this.summary.seats) || {};
    },
    /** Add-on slots the chosen plan allows; seats beyond the one included. */
    slots() {
      const plan = this.chosen.raw || {};
      return plan.addOnSlots != null ? plan.addOnSlots : 0;
    },
    currentExtra() {
      return Math.max(0, Number(this.seats.extra || 0));
    },
    seatPrice() {
      const price = Number(this.seats.price);
      return isFinite(price) && price > 0 ? price : 0;
    },
    multiplier() {
      // Yearly is ten months (§13.0a: two months free), for plan and seats alike.
      return this.interval === "year" ? 10 : 1;
    },
    planAmount() {
      const plan = this.chosen.raw || {};
      const monthly = Number(plan.priceMonthly);
      if (!isFinite(monthly)) return 0;
      return monthly * this.multiplier;
    },
    seatAmount() {
      return this.extra * this.seatPrice * this.multiplier;
    },
    extra() {
      return this.extraSeats == null ? this.currentExtra : this.extraSeats;
    },
    total() {
      return this.planAmount + this.seatAmount;
    },
    monthlyTotal() {
      const plan = this.chosen.raw || {};
      return (Number(plan.priceMonthly) || 0) + this.extra * this.seatPrice;
    },
    buttonLabel() {
      if (this.subscribe) return "Subscribe";
      return this.changed ? "Review change" : "This is what you pay now";
    },
    changed() {
      return this.chosen.planKey !== this.currentPlanKey
        || this.interval !== this.currentInterval
        || this.extra !== this.currentExtra;
    },
    currentInterval() {
      return (this.summary && this.summary.interval) || "month";
    },
    slotLine() {
      if (!this.slots) return "This plan has no add-on slots";
      return this.extra >= this.slots
        ? `All ${this.slots} slots used on this plan`
        : `${this.extra} of ${this.slots} add-on slots on this plan`;
    },
    slotPct() {
      if (!this.slots) return "0%";
      return `${Math.round((this.extra / this.slots) * 100)}%`;
    },
    seatLine() {
      const each = this.money(this.seatPrice * this.multiplier);
      return this.extra === 0
        ? "None beyond the one included"
        : `${this.extra} add-on at ${each}${this.interval === "year" ? " a year" : ""}`;
    },
    /**
     * What the change means against what they pay today. Spelled out rather than left for the
     * reader to subtract, and never guessing at proration Stripe will compute exactly.
     */
    delta() {
      if (!this.changed) {
        const label = this.currentInterval === "year" ? "yearly" : "monthly";
        return `This is your plan today: ${this.currentPlanName}${this.currentExtra ? ` with ${this.currentExtra} add-on seat${this.currentExtra === 1 ? "" : "s"}` : ""}, billed ${label}.`;
      }
      // A workspace that pays nothing today is not comparing two bills, it is seeing a price.
      // Quote it in the period they picked, or the sentence contradicts the total above it.
      if (this.currentMonthly === 0) {
        const price = this.interval === "year"
          ? `${this.money(this.total)} a year, the same as ${this.money(this.monthlyTotal)} a month with two months free`
          : `${this.money(this.total)} a month`;
        return `${price}. ${this.currentPlanName} costs you nothing today.`;
      }
      if (this.interval === "year" && this.currentInterval !== "year") {
        const saving = this.monthlyTotal * 12 - this.total;
        return saving > 0
          ? `Paying yearly is ${this.money(saving)} less than twelve monthly payments. It starts at your next renewal.`
          : "Paying yearly starts at your next renewal.";
      }
      const today = this.currentMonthly;
      if (today == null) return "The billing portal confirms the exact amount before anything is charged.";
      const difference = this.monthlyTotal - today;
      if (Math.abs(difference) < 0.005) return "Same as you pay today.";
      return difference > 0
        ? `${this.money(difference)} a month more than today. An upgrade starts at once, charged for the days left in this period.`
        : `${this.money(Math.abs(difference))} a month less than today. A downgrade starts at your next renewal.`;
    },
    /** Today's whole bill: plan plus the seats already assigned. Free is zero, not unknown. */
    currentMonthly() {
      const plan = this.plans.find(p => p.planKey === this.currentPlanKey);
      if (!plan) return null;
      const base = isFinite(Number(plan.priceMonthly)) ? Number(plan.priceMonthly) : 0;
      if (!plan.sellable && base === 0 && this.currentPlanKey !== "free") return null;
      return base + this.currentExtra * this.seatPrice;
    },
  },
  methods: {
    rank(key) {
      const i = ORDER.indexOf(String(key || "").toLowerCase());
      return i < 0 ? ORDER.length : i;
    },
    planName(key) {
      const k = String(key || "");
      return k.charAt(0).toUpperCase() + k.slice(1);
    },
    money(value) {
      const symbols = { usd: "US$", sgd: "S$", myr: "RM" };
      const currency = (this.summary && this.summary.currency) || "usd";
      const symbol = symbols[String(currency).toLowerCase()] || `${String(currency).toUpperCase()} `;
      const n = Number(value);
      if (!isFinite(n)) return "";
      const whole = Math.abs(n - Math.round(n)) < 0.005;
      return symbol + n.toLocaleString("en-US", {
        minimumFractionDigits: whole ? 0 : 2,
        maximumFractionDigits: whole ? 0 : 2,
      });
    },
  },
  watch: {
    // A plan with fewer slots than the seats being estimated cannot hold them.
    slots(value) {
      if (this.extra > value) this.extraSeats = value;
    },
  },
  created() {
    this.planKey = this.defaultPick;
    this.interval = this.currentInterval;
    this.extraSeats = this.currentExtra;
  },
};
</script>
